import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { 
  Edit, 
  Trash2,
  CalendarIcon,
  Building2,
  CheckCircle,
  Circle,
  Clock,
  Truck,
  Eye,
  StickyNote
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { statuses, categoryLabels } from './constants';

const formatDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' });
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'Australia/Sydney' });
};

export const WorkflowCard = memo(({ 
  workflow, 
  onEdit, 
  onDelete, 
  onViewDetails,
  isBarbiePink = false 
}) => {
  const navigate = useNavigate();
  
  const completedMilestones = workflow.milestones?.filter(m => m.status === 'completed').length || 0;
  const totalMilestones = workflow.milestones?.length || 0;
  const progressPercent = totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;
  
  // Calculate days until/since photo day
  const getPhotoDayStatus = () => {
    if (!workflow.photography_date) return null;
    const photoDate = new Date(workflow.photography_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    photoDate.setHours(0, 0, 0, 0);
    const diffTime = photoDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: `${Math.abs(diffDays)}d ago`, variant: 'secondary' };
    } else if (diffDays === 0) {
      return { text: 'Today!', variant: 'destructive' };
    } else if (diffDays <= 7) {
      return { text: `${diffDays}d`, variant: 'destructive' };
    } else if (diffDays <= 14) {
      return { text: `${diffDays}d`, variant: 'warning' };
    }
    return { text: `${diffDays}d`, variant: 'outline' };
  };
  
  const photoDayStatus = getPhotoDayStatus();
  const statusConfig = statuses[workflow.status] || statuses.pending;
  const categoryLabel = categoryLabels[workflow.category] || workflow.category;
  
  return (
    <Card 
      className={cn(
        "hover:shadow-md transition-all cursor-pointer",
        workflow.status === 'completed' && "opacity-60"
      )}
      onClick={() => onViewDetails(workflow)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left side - Main info */}
          <div className="flex-1 min-w-0">
            {/* Organization name - clickable */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/organizations?id=${workflow.org_id}`);
              }}
              className={cn(
                "font-semibold text-left hover:underline truncate block max-w-full",
                isBarbiePink ? "text-pink-600 hover:text-pink-700" : "text-primary hover:text-primary/80"
              )}
            >
              {workflow.org_name || 'Unknown Organization'}
            </button>
            
            {/* Category and Year */}
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Badge variant="outline" className="text-xs">
                {categoryLabel}
              </Badge>
              <span>{workflow.year}</span>
            </div>
            
            {/* Dates */}
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center gap-1">
                    <CalendarIcon className="h-3 w-3" />
                    <span>{formatDisplayDate(workflow.photography_date)}</span>
                    {photoDayStatus && (
                      <Badge variant={photoDayStatus.variant} className="text-[10px] px-1 py-0 ml-1">
                        {photoDayStatus.text}
                      </Badge>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>Photo Day</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {workflow.dispatch_date && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1">
                      <Truck className="h-3 w-3" />
                      <span>{formatDisplayDate(workflow.dispatch_date)}</span>
                    </TooltipTrigger>
                    <TooltipContent>Dispatch Date</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            
            {/* Progress */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{completedMilestones}/{totalMilestones}</span>
              </div>
              <Progress 
                value={progressPercent} 
                className="h-1.5"
              />
            </div>
          </div>
          
          {/* Right side - Status and Actions */}
          <div className="flex flex-col items-end gap-2">
            <Badge 
              variant={statusConfig.variant}
              className="flex items-center gap-1"
            >
              {workflow.status === 'completed' ? (
                <CheckCircle className="h-3 w-3" />
              ) : workflow.status === 'in_progress' ? (
                <Clock className="h-3 w-3" />
              ) : (
                <Circle className="h-3 w-3" />
              )}
              {statusConfig.label}
            </Badge>
            
            {/* Action buttons */}
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onViewDetails(workflow)}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View Details</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onEdit(workflow)}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => onDelete(workflow)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            {/* Notes indicator */}
            {workflow.notes && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <StickyNote className="h-3.5 w-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    {workflow.notes.length > 100 
                      ? workflow.notes.substring(0, 100) + '...' 
                      : workflow.notes
                    }
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

WorkflowCard.displayName = 'WorkflowCard';

export default WorkflowCard;
